import { type Question, type QuestionStats, percentage } from "../../shared";
import { AnimatedNumber, Bump, cx, fillStyle, useBumps } from "../ui";

type Props = {
	question: Question;
	/** What the server has for this participant; undefined until they answered. */
	answer: string[] | undefined;
	stats: QuestionStats | undefined;
	onVote: (optionIds: string[]) => void;
};

/**
 * The answers double as the result chart: once the participant has voted, every option fills up
 * with its live share, and a "+1" drifts up whenever someone else picks it.
 */
export function AnswerOptions({ question, answer, stats, onVote }: Props) {
	const multi = question.kind === "multi";
	const max = question.maxChoices ?? question.options.length;
	const submitted = !!answer?.length;
	const selection = answer ?? [];
	const bumps = useBumps(stats?.counts);
	const leaderShare = Math.max(1, ...question.options.map((option) => percentage(stats, option.id)));

	const choose = (optionId: string) => {
		if (!multi) {
			onVote([optionId]);
			return;
		}

		const next = selection.includes(optionId)
			? selection.filter((id) => id !== optionId)
			: [...selection, optionId];
		// The first tap is the answer, same as a single choice. Later taps add or remove options;
		// the last remaining one stays, because an empty selection is not a vote.
		if (next.length === 0 || next.length > max) {
			return;
		}
		onVote(next);
	};

	if (question.kind === "scale") {
		return (
			<ul className="grid grid-cols-5 gap-2 sm:gap-3">
				{question.options.map((option) => {
					const share = percentage(stats, option.id);
					return (
						<li key={option.id}>
							<button
								type="button"
								className="scale-tile w-full"
								aria-pressed={selection.includes(option.id)}
								onClick={() => choose(option.id)}
							>
								<span
									className="scale-fill"
									style={fillStyle(submitted ? (share / leaderShare) * 100 : 0)}
									aria-hidden="true"
								/>
								{submitted && (
									<span className="relative text-sm font-semibold">
										<AnimatedNumber value={share} /> %
									</span>
								)}
								<span className="relative text-3xl leading-none sm:text-4xl" aria-hidden="true">
									{option.emoji}
								</span>
								<span className="relative text-xs font-medium leading-tight sm:text-sm">{option.label}</span>
								<Bump token={submitted ? bumps[option.id] : undefined} className="top-0.5 right-1.5 text-primary" />
							</button>
						</li>
					);
				})}
			</ul>
		);
	}

	return (
		<div>
			<ul className="grid gap-2.5">
				{question.options.map((option) => {
					const selected = selection.includes(option.id);
					const share = percentage(stats, option.id);
					return (
						<li key={option.id}>
							<button
								type="button"
								className="option-card"
								aria-pressed={selected}
								disabled={multi && !selected && selection.length >= max}
								onClick={() => choose(option.id)}
							>
								<span className="option-fill" style={fillStyle(submitted ? share : 0)} aria-hidden="true" />
								{multi && (
									<span className="option-check" aria-hidden="true">
										{selected ? "✓" : ""}
									</span>
								)}
								<span className="relative text-2xl leading-none" aria-hidden="true">
									{option.emoji}
								</span>
								<span className="relative flex-1 font-medium leading-snug">{option.label}</span>
								{submitted && (
									<span className="relative w-14 shrink-0 text-right text-sm font-semibold">
										<AnimatedNumber value={share} /> %
									</span>
								)}
								<Bump token={submitted ? bumps[option.id] : undefined} className="top-2 right-18 text-primary" />
							</button>
						</li>
					);
				})}
			</ul>

			{multi && (question.maxChoices !== undefined || selection.length > 0) && (
				<p className={cx("mt-4 text-sm", selection.length >= max ? "font-medium text-foreground" : "text-muted-foreground")}>
					{question.maxChoices ? `${selection.length} von ${max} ausgewählt` : `${selection.length} ausgewählt`}
				</p>
			)}
		</div>
	);
}
